import { useEffect, useState, useCallback } from "react";
import { usePageMeta } from "@/lib/seo";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { apiClient } from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function Shop() {
  usePageMeta("Shop Research Peptides", "Browse our full catalog of Research Use Only peptides, each with batch-specific COA and independent lab verification.", "/shop");
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");

  const category = searchParams.get("category") || "";
  const sort = searchParams.get("sort") || "newest";
  const page = parseInt(searchParams.get("page") || "1", 10);

  useEffect(() => {
    apiClient.get("/categories").then((res) => setCategories(res.data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 12, sort };
      if (category) params.category = category;
      if (search) params.search = search;
      const { data } = await apiClient.get("/products", { params });
      setProducts(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [category, sort, page, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c.slug]));
  const pages = Math.ceil(total / 12);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16" data-testid="shop-page">
      <div className="mb-10 text-center">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">Research Compounds</span>
        <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900 font-display">Shop Research Peptides</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-64 shrink-0 space-y-6">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Categories
            </h3>
            <div className="space-y-1.5">
              <button
                onClick={() => updateParam("category", "")}
                className={`block w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-300 ${!category ? "bg-gradient-to-r from-blue-900 to-blue-500 text-white shadow-blue-glow-sm" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"}`}
                data-testid="category-filter-all"
              >
                All Compounds
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => updateParam("category", c.slug)}
                  className={`block w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-300 ${category === c.slug ? "bg-gradient-to-r from-blue-900 to-blue-500 text-white shadow-blue-glow-sm" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"}`}
                  data-testid={`category-filter-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-8">
            <form onSubmit={(e) => { e.preventDefault(); updateParam("search", search); }} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search research compounds..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 rounded-full"
                data-testid="shop-search-input"
              />
            </form>
            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
              <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="shop-sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-loading">
              {Array.from({ length: 6 }).map((_, i) => <div key={`shop-skeleton-${i}`} className="h-96 bg-slate-100 rounded-2xl animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 text-slate-500" data-testid="shop-empty-state">No research compounds match your filters.</div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-5" data-testid="shop-results-count">{total} compound{total !== 1 ? "s" : ""} found</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p) => <ProductCard key={p.slug} product={p} categorySlug={categoryMap[p.category_id]} />)}
              </div>
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-12" data-testid="shop-pagination">
                  {Array.from({ length: pages }).map((_, i) => (
                    <Button
                      key={`shop-page-${i + 1}`}
                      variant={page === i + 1 ? "default" : "outline"}
                      size="sm"
                      className="rounded-full w-9"
                      onClick={() => updateParam("page", String(i + 1))}
                      data-testid={`pagination-page-${i + 1}`}
                    >
                      {i + 1}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
